import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import Tabs from '../../components/ui/Tabs';
import { BULK_CATEGORY_TABS, BULK_CATEGORY_HINTS } from '../../utils/bulkRequestCategories';
import Select from '../../components/ui/Select';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import { listBulkRequestTypes } from '../../api/masters';
import { listBulkRequestCandidates, createBulkRequest } from '../../api/bulkRequests';
import { pushToast } from '../../components/ui/toastBus';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';

export default function BulkRequest() {
  const queryClient = useQueryClient();

  const filters = useCandidateFilters();
  const { categoryIds, stepIds } = filters;
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkRequestTypeId, setBulkRequestTypeId] = useState('');
  const [note, setNote] = useState('');
  const [requestTab, setRequestTab] = useState('action');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const typesQuery = useQuery({ queryKey: ['bulk-request-types'], queryFn: () => listBulkRequestTypes() });

  // A fresh filter invalidates whatever was picked under the previous workflow/step.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [categoryIds, stepIds]);

  const candidatesQuery = useQuery({
    queryKey: ['bulk-request-candidates', categoryIds, stepIds, search],
    queryFn: () =>
      listBulkRequestCandidates({
        workflow_category_ids: categoryIds.length ? categoryIds : undefined,
        workflow_step_ids: stepIds.length ? stepIds : undefined,
        search: search || undefined,
      }),
  });
  const candidates = useMemo(() => candidatesQuery.data || [], [candidatesQuery.data]);

  const allShownSelected = candidates.length > 0 && candidates.every((c) => selectedIds.has(c.id));

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllShown = () => {
    setSelectedIds((prev) => {
      if (allShownSelected) {
        const next = new Set(prev);
        candidates.forEach((c) => next.delete(c.id));
        return next;
      }
      const next = new Set(prev);
      candidates.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const submitMutation = useMutation({
    mutationFn: () =>
      createBulkRequest({
        bulk_request_type_id: bulkRequestTypeId,
        workflow_category_ids: categoryIds,
        workflow_step_ids: stepIds,
        note: note.trim() || undefined,
        candidate_ids: Array.from(selectedIds),
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: `Bulk request submitted for ${selectedIds.size} candidate(s).` });
      setSelectedIds(new Set());
      setNote('');
      setBulkRequestTypeId('');
      queryClient.invalidateQueries({ queryKey: ['bulk-requests'] });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not submit bulk request.' }),
  });

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={allShownSelected}
          onChange={toggleAllShown}
          aria-label="Select all shown candidates"
        />
      ),
      render: (row) => (
        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleOne(row.id)} aria-label={`Select ${row.full_name}`} />
      ),
    },
    { key: 'reference_no', label: 'Reference No' },
    { key: 'full_name', label: 'Full Name' },
    { key: 'workflow_category', label: 'Workflow' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'contact_no', label: 'Contact' },
    {
      key: 'current_step',
      label: 'Current Step',
      render: (row) => row.current_step?.title || <span className="text-gray-400">—</span>,
    },
  ];

  const canSubmit = selectedIds.size > 0 && !!bulkRequestTypeId;

  return (
    <div>
      <PageHeader
        title="Bulk Request"
        description="Filter candidates by vehicle type, location, license type, workflow and step (each can take several values), select multiple, and raise one request across all of them."
      />

      <CandidateFilterBar {...filters.barProps}>
        <Input
          placeholder="Search name, reference, contact…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-72"
        />
      </CandidateFilterBar>

      {(
        <>
          <div className="mb-2 text-sm text-gray-500">
            {selectedIds.size > 0 ? <span className="font-medium text-gray-700">{selectedIds.size} selected</span> : 'No candidates selected'}
            {' · '}
            {candidates.length} shown
          </div>
          <SearchSummary search={search} total={candidates.length} rows={candidates} />

          <Table
            columns={withMatchColumn(columns, search)}
            data={candidates}
            isLoading={candidatesQuery.isLoading}
            emptyMessage="No candidates match this filter."
          />

          <Card className="mt-4">
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Raise request</h2>
            <Tabs
              tabs={BULK_CATEGORY_TABS}
              active={requestTab}
              onChange={(v) => {
                setRequestTab(v);
                setBulkRequestTypeId('');
              }}
            />
            <p className="mb-3 mt-3 text-xs text-gray-500">{BULK_CATEGORY_HINTS[requestTab]}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select label="Request Type" value={bulkRequestTypeId} onChange={(e) => setBulkRequestTypeId(e.target.value)}>
                <option value="">Select a type…</option>
                {(typesQuery.data || []).filter((t) => (t.category || 'action') === requestTab).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
              <Textarea label="Note (optional)" rows={1} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending} disabled={!canSubmit}>
                Submit request for {selectedIds.size || 0} candidate(s)
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
